import signApi from "../utils/api/signature";
import { LOGOUT } from "./types";

const appendEnvelopeFormValue = (form, key, value) => {
  if (value === undefined || value === null || value === "") return;
  const isFile = typeof File !== "undefined" && value instanceof File;
  form.append(key, typeof value === "object" && !isFile ? JSON.stringify(value) : value);
};

const envelopeRequestBody = ({ file, recipients, documents, ...payload }) => {
  if (!file) {
    return Object.entries(payload).reduce((result, [key, value]) => {
      if (value !== undefined && value !== null && value !== "") result[key] = value;
      return result;
    }, {});
  }

  const form = new FormData();
  form.append("file", file, file.name);
  Object.entries(payload).forEach(([key, value]) => appendEnvelopeFormValue(form, key, value));
  return form;
};

const templateUsePayload = ({ recipients = [], roleMappings, signers, fields, data, ...payload }) => {
  const contextualSigners = Array.isArray(signers) && signers.length
    ? signers.map((signer) => ({
      ...(signer?.signer && typeof signer.signer === "object" ? signer.signer : {}),
      ...signer,
      signer: undefined,
    }))
    : recipients.map((recipient, index) => ({
      ...recipient,
      roleKey: recipient.roleKey || recipient.key || recipient.role || `recipient${index + 1}`,
      context: recipient.context || recipient.signerContext || undefined,
    }));

  return {
    ...payload,
    roleMappings: Array.isArray(roleMappings) ? roleMappings : undefined,
    signers: contextualSigners.length ? contextualSigners : undefined,
    fields: fields || data || undefined,
    data: data || undefined,
  };
};

const templateUseError = (error, fallback) =>
  error?.response?.data?.error || error?.message || fallback;

export const useTemplate = ({ templateUuid, ...payload } = {}) => async (dispatch) => {
  try {
    if (!templateUuid) throw new Error("A template UUID is required.");
    const response = await signApi.post(
      `/signature/templates/${templateUuid}/use`,
      templateUsePayload(payload)
    );
    return response.data || response;
  } catch (error) {
    if (error?.response?.status === 401) dispatch({ type: LOGOUT });
    return { success: false, error: templateUseError(error, "Unable to create an envelope from the signature template.") };
  }
};

export const createEnvelope = (payload = {}) => async (dispatch) => {
  try {
    const { recipients = [], selfSign = false } = payload;
    const documents = Array.isArray(payload.documents) ? payload.documents.filter(Boolean) : [];
    if (payload.templateUuid && !payload.file && !payload.sourceFileUuid) {
      return await dispatch(useTemplate({
        ...payload,
        templateUuid: payload.templateUuid,
        sourceApplication: payload.sourceApplication || "matters.firm",
      }));
    }
    const firstDocument = documents[0] || null;
    const endpoint = selfSign ? "/signature/envelopes/self-sign" : "/signature/envelopes";
    const requestBody = envelopeRequestBody({
      ...payload,
      sourceFileUuid: payload.sourceFileUuid || firstDocument?.sourceFileUuid,
      originalFileName: payload.originalFileName || firstDocument?.originalFileName || firstDocument?.fileName || firstDocument?.name,
      fileName: payload.fileName || firstDocument?.fileName || firstDocument?.originalFileName || firstDocument?.name,
      displayName: payload.displayName || firstDocument?.displayName || firstDocument?.originalFileName || firstDocument?.fileName || firstDocument?.name,
    });
    const requestConfig = typeof FormData !== "undefined" && requestBody instanceof FormData
      ? { headers: { "Content-Type": "multipart/form-data" } }
      : undefined;
    const response = await signApi.post(endpoint, requestBody, requestConfig);

    const responsePayload = response.data || response;
    const envelopeUuid = responsePayload?.item?.uuid || responsePayload?.uuid || null;

    if (responsePayload?.success !== false && envelopeUuid && documents.length > 1) {
      for (const [index, document] of documents.slice(1).entries()) {
        const documentResponse = await signApi.post(`/signature/envelopes/${envelopeUuid}/documents`, {
          ...document,
          documentOrder: index + 1
        });

        if (documentResponse.data?.success === false) {
          throw new Error(documentResponse.data?.error?.message || documentResponse.data?.error || "Unable to add a document to the signature envelope.");
        }
      }
    }

    if (!selfSign && responsePayload?.success !== false && envelopeUuid && recipients.length > 0) {
      for (const [index, recipient] of recipients.entries()) {
        await signApi.post(`/signature/envelopes/${envelopeUuid}/recipients`, {
          recipientType: "signer",
          isRequired: true,
          routingOrder: index + 1,
          ...recipient
        });
      }
    }

    return responsePayload;
  } catch (error) {
    if (error?.response?.status === 401) {
      dispatch({ type: LOGOUT });
    }

    return {
      success: false,
      error: error?.response?.data?.error || error?.message || "Unable to create signature envelope."
    };
  }
};

export const selfSignEnvelope = (payload = {}) => createEnvelope({
  ...payload,
  selfSign: true,
  creationSource: "self_sign"
});

export const getEnvelope = ({ uuid }) => async (dispatch) => {
  try {
    const response = await signApi.get(`/signature/envelopes/${uuid}`);
    return response.data || response;
  } catch (error) {
    if (error?.response?.status === 401) {
      dispatch({ type: LOGOUT });
    }

    return {
      success: false,
      error: error?.response?.data?.error || error?.message || "Unable to load signature envelope."
    };
  }
};

export const listEnvelopes = ({
  ownerPersonUuid,
  personUuid,
  ownerUuid,
  contextUuid,
  matterUuid,
  leadUuid,
  firmUuid,
  senderUuid,
  sourceSystem,
  apiClientId,
  category,
  practiceArea,
  documentType,
  status,
  limit = 100
} = {}) => async (dispatch) => {
  try {
    const response = await signApi.get("/signature/envelopes", {
      params: {
        ownerPersonUuid,
        personUuid,
        ownerUuid,
        contextUuid,
        matterUuid,
        leadUuid,
        firmUuid,
        senderUuid,
        sourceSystem,
        apiClientId,
        category,
        practiceArea,
        documentType,
        status,
        limit
      }
    });
    return response.data || response;
  } catch (error) {
    if (error?.response?.status === 401) {
      dispatch({ type: LOGOUT });
    }

    return {
      success: false,
      error: error?.response?.data?.error || error?.message || "Unable to load signature requests."
    };
  }
};

export const duplicateEnvelope = ({ uuid }) => async (dispatch) => {
  try {
    const response = await signApi.post(`/signature/envelopes/${uuid}/duplicate`);
    return response.data || response;
  } catch (error) {
    if (error?.response?.status === 401) {
      dispatch({ type: LOGOUT });
    }

    return {
      success: false,
      error: error?.response?.data?.error || error?.message || "Unable to duplicate signature request."
    };
  }
};

export const remindEnvelope = ({ uuid }) => async (dispatch) => {
  try {
    const response = await signApi.post(`/signature/envelopes/${uuid}/remind`);
    return response.data || response;
  } catch (error) {
    if (error?.response?.status === 401) {
      dispatch({ type: LOGOUT });
    }

    return {
      success: false,
      error: error?.response?.data?.error || error?.message || "Unable to send signature reminder."
    };
  }
};

export const deleteEnvelopeGlobally = ({ uuid }) => async (dispatch) => {
  try {
    const response = await signApi.delete(`/signature/envelopes/${uuid}/global`);
    return response.data || response;
  } catch (error) {
    if (error?.response?.status === 401) {
      dispatch({ type: LOGOUT });
    }

    return {
      success: false,
      error: error?.response?.data?.error || error?.message || "Unable to delete signature request."
    };
  }
};
